import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { Switch } from '@/components/forms/Switch';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { clientsAssetsApi } from '@/api/services/clientsAssets';
import { SummaryTab } from './SummaryTab';
import { DetailedTab } from './DetailedTab';
import { clampDate, todayIso, yesterdayIso } from './helpers';
import './ClientsAssets.css';

const TABS: TabItem[] = [
  { key: 'ca-summary', title: 'Summary Report' },
  { key: 'ca-detailed', title: 'Detailed Report' },
];

export function ClientsAssetsPage() {
  const toast = useToast();
  const [params] = useSearchParams();

  // Initial filter state: ?live=1 forces live+today; ?date= (clamped) else yesterday.
  const initialLive = params.get('live') === '1';
  const initialDate = initialLive ? todayIso() : (clampDate(params.get('date')) ?? yesterdayIso());

  const [referenceDate, setReferenceDate] = useState(initialDate);
  const [isLive, setIsLive] = useState(initialLive);

  // Applied values the tabs actually load from (Fetch commits the pending filter).
  const [applied, setApplied] = useState({ referenceDate: initialDate, isLive: initialLive });
  const [reloadKey, setReloadKey] = useState(0);
  const [active, setActive] = useState('ca-summary');

  // syncLiveToDate: the Live switch reflects "is referenceDate today?".
  const lastDate = useRef(referenceDate);
  useEffect(() => {
    if (lastDate.current !== referenceDate) {
      lastDate.current = referenceDate;
      setIsLive(referenceDate === todayIso());
    }
  }, [referenceDate]);

  function onToggleLive(next: boolean) {
    setIsLive(next);
    if (next) {
      setReferenceDate(todayIso());
      lastDate.current = todayIso();
    } else if (referenceDate === todayIso()) {
      setReferenceDate(yesterdayIso());
      lastDate.current = yesterdayIso();
    }
  }

  function fetchData() {
    setApplied({ referenceDate, isLive });
    setReloadKey((k) => k + 1);
  }

  async function extract() {
    try {
      await clientsAssetsApi.extractExcel({ referenceDate: applied.referenceDate, isLive: applied.isLive }, 'Clients Assets.xlsx');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to extract the report.');
    }
  }

  const fields = [
    {
      node: (
        <DateRangePicker
          key="ref-date"
          label="Reference Date"
          mode="single"
          value={referenceDate}
          maxDate={todayIso()}
          onChange={setReferenceDate}
        />
      ),
      pinned: true,
      width: 160,
    },
  ];
  const modes = <Switch label="Live Data" checked={isLive} onChange={onToggleLive} />;
  const actions = (
    <>
      <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" onClick={fetchData}>
        Fetch
      </AsyncButton>
      <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={extract}>
        Extract
      </AsyncButton>
    </>
  );

  return (
    <div className="clients-assets-page">
      <PageHeader title="Client Assets" breadcrumb={['Home', 'Client Assets']} />

      <div className="card custom-card mb-0">
        <div className="card-header border-bottom">
          <FilterBar card={false} fields={fields} modes={modes} actions={actions} inlineActions />
        </div>
        <div className="card-header p-0">
          <Tabs tabs={TABS} active={active} onChange={setActive} variant="underline" />
        </div>
        <div className="card-body">
          {active === 'ca-summary' && (
            <SummaryTab referenceDate={applied.referenceDate} isLive={applied.isLive} reloadKey={reloadKey} />
          )}
          {active === 'ca-detailed' && (
            <DetailedTab referenceDate={applied.referenceDate} isLive={applied.isLive} reloadKey={reloadKey} />
          )}
        </div>
      </div>
    </div>
  );
}
